<template>
	<slideover :open="show" title="Add New Revision" @close="emit('close')">
		<div class="p-4 space-y-4">
			<div class="flex space-x-4">
				<app-input
					v-model="form.revision_no"
					label="Revision No"
					:error="errors.revision_no"
					class="w-1/3"
					disabled />
				<app-input
					v-model="form.description"
					label="Revision Description"
					:error="errors.description"
					placeholder="Enter description"
					class="w-2/3" />
			</div>

			<div class="my-5">
				<app-file-button
					name="Add"
					:accept="'.' + (document.type?.allowable_ext || 'pdf')"
					:error="errors.file"
					@file-change="onFileChange">
					{{ form.file ? 'Change File' : 'Select File' }}
				</app-file-button>
				<p v-if="form.file" class="mt-2 text-sm text-gray-600">
					Selected: {{ form.file.original_name || form.file.name }}
				</p>
				<p v-if="errors.file" class="mt-2 text-sm text-red-600">{{ errors.file }}</p>
			</div>
		</div>

		<template #action>
			<div class="w-full flex justify-end">
				<app-button :loading="loading" theme="primary" @click="submit"> Save </app-button>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch } from 'vue'
	import Slideover from '@/components/partials/Slideover.vue'
	import AppButton from '@/components/base/AppButton.vue'
	import AppInput from '@/components/base/forms/AppInput.vue'
	import AppFileButton from '@/components/base/forms/AppFileButton.vue'
	import DocumentService from '@/services/document'
	import FileService from '@/services/file'
	import type { Document } from '@/data/schema/document'
	import { YMD } from '@/utils/date'

	const props = defineProps<{
		show: boolean
		document: Document
	}>()

	const emit = defineEmits(['close', 'revision-added'])

	const form = ref<any>({
		revision_no: '',
		date: YMD(new Date()),
		description: '',
		file: null
	})
	const errors = ref<any>({})
	const loading = ref(false)

	const calculateNextRevision = (doc: Document) => {
		if (!doc || !doc.revisions || doc.revisions.length === 0) {
			return '1'
		}
		const maxRev = doc.revisions.reduce((max, current) => {
			const currentNum = parseInt(current.revision_no)
			return !isNaN(currentNum) && currentNum > max ? currentNum : max
		}, 0)
		return (maxRev + 1).toString()
	}

	watch(
		() => props.document,
		(newDoc) => {
			if (newDoc) {
				form.value.revision_no = calculateNextRevision(newDoc)
				form.value.date = YMD(new Date())
				form.value.description = ''
				form.value.file = null
				errors.value = {}
			}
		},
		{ immediate: true }
	)

	const onFileChange = async (files: File[]) => {
		// Clear any existing file error when file selection starts
		if (errors.value.file) {
			delete errors.value.file
		}
		
		if (files && files.length > 0) {
			const file = files[0]
			
			// Validate file extension - get from document type
			const allowableExt = props.document.type?.allowable_ext || 'pdf'
			const fileExtension = file.name.split('.').pop()?.toLowerCase()
			if (fileExtension !== allowableExt.toLowerCase()) {
				errors.value.file = `Invalid file type. Only .${allowableExt} files are allowed.`
				return
			}
			
			const fileService = new FileService()
			try {
				const res = await fileService.upload(file)
				form.value.file = res.data.file
			} catch (error) {
				console.error('Error uploading file:', error)
				// Handle upload error
			}
		} else {
			form.value.file = null
		}
	}

	const submit = async () => {
		errors.value = {}

		if (!form.value.file) {
			errors.value = { file: ['The file field is required.'] }
			return
		}

		loading.value = true
		const documentService = new DocumentService()
		try {
			await documentService.addRevision(props.document.id, form.value)
			emit('revision-added')
			emit('close')
		} catch (error) {
			if (error.response?.data?.errors) {
				errors.value = error.response.data.errors
			}
			console.error('Error adding revision:', error)
		} finally {
			loading.value = false
		}
	}
</script>
